import { Box, Typography, Tooltip, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useAuth from 'src/hooks/useAuth';
import { Help } from '@mui/icons-material';
import { checkRoute } from 'src/utils/permissions';
import { isBlockedByRecipientEin } from 'src/utils/einStatus';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const TAX_SETTINGS_PATH = PATH_DASHBOARD.settings.business(
  'settings',
  'tax-settings'
);

export default function Paywall({ children }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const history = useHistory();
  const { pathname: path } = useLocation();

  const hasPerms = useMemo(() => checkRoute(user, path), [user, path]);

  useEffect(() => {
    if (user?.is_corporate && path === PATH_DASHBOARD.root) {
      history.push(PATH_DASHBOARD.corporate.home);
      return;
    }

    // Check if the current path is under any corporate-related paths
    const isCorporatePath = Object.values(PATH_DASHBOARD.corporate).some(
      (corporatePath) => path.startsWith(corporatePath)
    );

    if (isCorporatePath && !user?.is_corporate) {
      // Redirect non-corporate users trying to access any corporate path to the root URL
      history.push(PATH_DASHBOARD.root);
      return;
    }

    if (!hasPerms && path === PATH_DASHBOARD.root) {
      history.push(PATH_DASHBOARD.settings.account('general'));
    }
  }, [history, path, hasPerms]);

  const blockedByRecipientEin =
    path.startsWith(PATH_DASHBOARD.freshcue.root) &&
    isBlockedByRecipientEin(user);

  if (hasPerms) return <>{children}</>;

  if (blockedByRecipientEin) {
    return (
      <Box
        width="100%"
        height="calc(100vh - 120px)"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        px={2}
      >
        <Typography variant="h4" gutterBottom>
          {t('paywall.ein-required')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 520 }}
        >
          {t('paywall.ein-required-description')}
        </Typography>
        {user?.is_admin ? (
          <Button
            variant="contained"
            component={RouterLink}
            to={TAX_SETTINGS_PATH}
          >
            {t('paywall.set-ein')}
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('paywall.ask-admin-ein')}
          </Typography>
        )}
      </Box>
    );
  }

  return !hasPerms && path === PATH_DASHBOARD.root ? (
    <></>
  ) : (
    <Box
      width="100%"
      height="calc(100vh - 120px)"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Typography>{t('paywall.need-subscription')}&nbsp;</Typography>
      <RouterLink to={PATH_DASHBOARD.allPlans}>
        {t('paywall.upgrade-to-unlock')}&nbsp;
      </RouterLink>
      <Tooltip title={t('paywall.already-subscribed-help')}>
        <Help color="disabled" />
      </Tooltip>
    </Box>
  );
}
